// Error Boundary component for React crash protection
// Must be a class component as error boundaries require getDerivedStateFromError

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { track } from '@/utils/analytics';
import { logger } from '@/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Get userId from localStorage for logging context
function getUserId(): string | null {
  try {
    const stored = localStorage.getItem('2tio-order-store');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.state?.userId || null;
    }
  } catch {
    // Ignore localStorage errors
  }
  return null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error({ area: 'boundary', userId: getUserId() || undefined }, error.message, error);
    track('checkout_error', {
      error_context: 'react_crash',
      error_message: error.message,
      component_stack: errorInfo.componentStack?.slice(0, 500),
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleStartOver = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary__content">
            <h2 className="error-boundary__title">Something went wrong</h2>
            <p className="error-boundary__message">
              We encountered an unexpected error. Please try again or start over.
            </p>
            <div className="error-boundary__actions">
              <button
                type="button"
                className="btn"
                onClick={this.handleRetry}
              >
                Try Again
              </button>
              <button
                type="button"
                className="btn btn--back"
                onClick={this.handleStartOver}
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
